<template>
  <div
    v-if="ready"
    @click.self="clearSelected"
    @keydown.space.prevent="clearSelected"
    :class="[
      'strengths-mapping',
      isMappingIndicators ? 'indicator-mapping' : '',
      'current-page-' + currentPageNumber,
      'previous-page-' + previousPageNumber,
      showOverlay ? 'show-overlay' : '',
      showMoreInfo ? 'show-more-info' : '',
      currentStrengthItem && currentStrengthItem.rating == null
        ? 'has-unrated-selected-strength'
        : '',
      !!currentStrengthBeingDragged ? 'is-dragging-strength' : '',
      !nextUnratedStrengthOnPage ? 'is-page-complete' : '',
      shouldDisplayPageCompleteMessage ||
      shouldDisplayReviewCompleteMessage ||
      shouldDisplaySectionCompleteMessage
        ? 'display-complete-message'
        : '',
      shouldDisplayReviewCompleteMessage ||
      shouldDisplaySectionCompleteMessage ||
      shouldDisplayPageCompleteMessage
        ? 'show-button'
        : '',
      isReviewComplete ? 'review-complete' : '',
      isSection2 ? 'section-2-theme' : '',
      isMobile ? 'strengths-mapping-mobile-view' : '',
    ]"
  >
    <!-- v-disable-pull-to-refresh-->
    <!--    <div class="mobile-scroll-wrapper hide-on-larger-devices">-->
    <!--      <p class="inverse">{{ reviewTitle }}</p>-->
    <!--      <p>Scroll down to begin rating<span class="glyphicon glyphicon-menu-down"></span></p>-->
    <!--      <a class="mobile-home-link" href="/">Home</a>-->
    <!--    </div>-->
    <div class="rating-interface-wrapper">
      <div class="rating-interface">
        <div class="strengths-mapping-navbar hide-on-small-devices">
          <div v-if="!isLiveEnvironment()" class="auto-complete-btn-container">
            <a
              class="btn btn-default"
              id="auto-complete-btn"
              href="javascript:void(0);"
              :data-test="
                strengthsMappingType === 'Strengths' ? 'auto-complete' : 'indicator-auto-complete'
              "
              @click="autoCompleteStrengthMapping"
              >Auto-complete</a
            >
          </div>
          <span
            ><strong data-test="strengths-mapping-title">{{ reviewTitle }}</strong></span
          >
          <a class="home-link" href="/" v-if="!embedded" data-test="strengths-mapping-home-link"
            >Home</a
          >
        </div>
        <div class="subnavbar">
          <div
            class="drag-start"
            @click.self="clearSelected"
            @keydown.space.prevent="clearSelected"
          >
            <div ref="nextUnratedStrengthOrigin" class="drag-origin-wrapper"></div>
            <button
              class="btn btn-lg btn-primary submit-button hide-on-small-devices"
              ref="submitButton"
              @click="buttonInfo.action()"
              :data-test="
                strengthsMappingType === 'Strengths'
                  ? 'screen-lg-submit'
                  : 'screen-lg-indicator-submit'
              "
              type="button"
            >
              {{ buttonInfo.bigScreenText }}
            </button>
            <StrengthsMappingPaging
              class="mobile-paging hide-on-larger-devices"
              :current-page-number="currentPageNumber"
              :min-page-number="minPageNumberForPaging"
              :max-page-number="maxPageNumber"
              :max-accessible-page-number="maxPageNumberForPaging"
              @previous-page="previousPage"
              @next-page="nextPage"
              @go-to-page="goToPage"
              :can-go-to-previous-page="canGoToPreviousPage"
              :can-go-to-next-page="canGoToNextPage"
              :page-details="pageDetails.slice(minPageNumberForPaging - 1, maxPageNumberForPaging)"
            />
          </div>
          <div class="current-strength">
            <div class="header subnavbar-header hide-on-small-devices">
              <div v-if="currentStrengthItem" class="header-text" data-test="sm-strength-progress">
                <span class="strength-name strength-name-text">{{
                  currentStrengthItem.strengthName
                }}</span>
                ({{ ratedStrengthsCount + 1 }}/<span data-test="number-of-rating-items">{{
                  allStrengthItems.length
                }}</span
                >)
              </div>
              <div v-if="shouldDisplayPageCompleteMessage" class="header-text">
                {{ pageCompleteText }}
              </div>
              <div v-if="shouldDisplaySectionCompleteMessage" class="header-text">
                You've completed the main review.
              </div>
            </div>
            <p v-if="currentStrengthItem">{{ currentStrengthItem.description }}</p>
            <p v-if="shouldDisplayPageCompleteMessage" class="page-complete-message">
              You're done with this page. You can still move the
              {{ strengthsMappingType === "Strengths" ? "strengths" : "items" }}, or click on them
              to review the definition.
            </p>
            <p v-if="shouldDisplaySectionCompleteMessage" class="page-complete-message">
              <span class="hide-on-larger-devices">
                <b>You've completed the main review.</b> Flip back through until you're satisfied
                with the ratings you gave, then continue.
              </span>
              <span class="hide-on-small-devices">
                Flip back through the pages until you're satisfied with how they have been placed,
                then continue.
              </span>
            </p>
            <p v-if="shouldDisplayReviewCompleteMessage" class="page-complete-message">
              <span class="hide-on-larger-devices">
                <b>You've completed the review.</b> Flip back through until you're satisfied with
                the ratings you gave, then press submit.
              </span>
              <span class="hide-on-small-devices">
                Flip back through the pages until you're satisfied with how the strengths have been
                placed before submitting.
              </span>
            </p>
            <div
              class="current-strength-more-info hover-wrapper"
              @click="showMoreInfo = !showMoreInfo"
              @keydown.space.prevent="showMoreInfo = !showMoreInfo"
            >
              <div class="header subnavbar-header">
                <span class="header-text more-information-header-text">More information</span>
              </div>
              <div class="more-info-checkbox-wrapper">
                <StyledCheckbox :modelValue="showMoreInfo" @update:modelValue="setShowMoreInfo" />
              </div>
            </div>
            <div class="mobile-submit-button-wrapper hide-on-larger-devices">
              <div class="mobile-submit-button-inner">
                <button
                  class="btn btn-sm btn-primary mobile-submit-button"
                  @click="buttonInfo.action()"
                  data-test="screen-sm-submit"
                  type="button"
                >
                  {{ buttonInfo.smallScreenText }}
                </button>
              </div>
            </div>
          </div>
          <StrengthsMappingPaging
            class="hide-on-small-devices"
            :current-page-number="currentPageNumber"
            :min-page-number="minPageNumberForPaging"
            :max-page-number="maxPageNumber"
            :max-accessible-page-number="maxPageNumberForPaging"
            @previous-page="previousPage"
            @next-page="nextPage"
            @go-to-page="goToPage"
            :can-go-to-previous-page="canGoToPreviousPage"
            :can-go-to-next-page="canGoToNextPage"
            :page-details="pageDetails.slice(minPageNumberForPaging - 1, maxPageNumberForPaging)"
          />
        </div>
        <div class="main-area" @click.self="clearSelected" @keydown.space.prevent="clearSelected">
          <div
            class="rating-area"
            @click.self="clearSelected"
            @keydown.space.prevent="clearSelected"
            data-test="rating-area"
          >
            <div ref="strengthDragHeightMeasure" class="strength-drag-height-measure"></div>
            <div ref="currentStrengthLine" class="current-strength-line hidden"></div>
            <div
              ref="ratingArea"
              class="ratings"
              @click.self="clearSelected"
              @keydown.space.prevent="clearSelected"
            >
              <transition-group name="transition-strengths" tag="div">
                <DraggableStrength
                  v-for="item in currentPageVisibleStrengths"
                  :key="item.strengthId"
                  :data-strength-id="item.strengthId"
                  :strength="item"
                  :is-current="item === currentStrengthItem"
                  @dragClick="onStrengthDragClick"
                  @dragStart="onStrengthDragStart"
                  @dragMove="onStrengthDragMove"
                  @dragEnd="onStrengthDragEnd"
                  :class="'strength-on-page-' + currentPageNumber"
                />
              </transition-group>
            </div>
            <div class="rating-scale">
              <div class="rating-scale-image"></div>
              <span>Not characteristic</span>
              <span>Less characteristic</span>
              <span>More characteristic</span>
              <span>Highly characteristic</span>
            </div>
            <div class="depth-overlay"></div>
            <a
              class="nav-arrow previous-nav-arrow"
              @click="previousPage()"
              @keydown.enter.prevent="previousPage()"
              :disabled="!canGoToPreviousPage"
              aria-label="Previous page"
            >
              <span class="glyphicon glyphicon-menu-left"></span>
            </a>
            <a
              class="nav-arrow next-nav-arrow"
              @click="handleNextArrowClick()"
              @keydown.enter.prevent="handleNextArrowClick()"
              :disabled="!canGoToNextPage"
              aria-label="Next page"
            >
              <span class="glyphicon glyphicon-menu-right"></span>
            </a>
          </div>
          <transition name="left-shutter-transition">
            <div
              class="shutter left-shutter"
              v-if="showMoreInfo"
              @click="showMoreInfo = false"
              @keydown.space.prevent="showMoreInfo"
            >
              <h4>
                Less characteristic of
                <span v-if="currentStrengthItem" class="strength-name-text">{{
                  currentStrengthItem.strengthName
                }}</span>
              </h4>
              <ul v-if="currentStrengthItem">
                <li :key="i" v-for="(indicator, i) in currentStrengthItem.negativeIndicators">
                  {{ indicator }}
                </li>
              </ul>
            </div>
          </transition>
          <transition name="right-shutter-transition">
            <div
              class="shutter right-shutter"
              v-if="showMoreInfo"
              @click="showMoreInfo = false"
              @keydown.space.prevent="showMoreInfo"
            >
              <h4>
                More characteristic of
                <span v-if="currentStrengthItem" class="strength-name-text">{{
                  currentStrengthItem.strengthName
                }}</span>
              </h4>
              <div class="mobile-more-info-float"></div>
              <ul v-if="currentStrengthItem">
                <li :key="i" v-for="(indicator, i) in currentStrengthItem.positiveIndicators">
                  {{ indicator }}
                </li>
              </ul>
            </div>
          </transition>
        </div>
        <div class="overlay" @click.self="hideOverlay" @keydown.space.self="hideOverlay">
          <div class="overlay-text">Drag the strengths from the top left corner onto the scale</div>
        </div>
      </div>
    </div>
    <div class="orientation-overlay">
      <div class="orientation-overlay-text">
        <img
          src="../../assets/strengths-mapping/request-landscape-orientation.svg"
          alt="Tilt device to landscape"
        />
        <span>Please turn your device</span>
        <span class="inverse">
          Make sure that your Orientation Lock
          <img src="../../assets/strengths-mapping/orientation-lock.svg" alt="" /> is off
        </span>
      </div>
    </div>
    <div class="unsupported-overlay">
      <div class="unsupported-overlay-text">
        <span>Please try on a larger or more modern device.</span>
      </div>
    </div>

    <div class="strengths-mapping-interstitial" v-if="shouldShowInterstitial">
      <cms-img :src="interstitialImageSrc" class="strengths-mapping-interstitial-image"></cms-img>
      <button
        class="strengths-mapping-interstitial-button btn btn-lg btn-primary"
        @click="hideInterstitial"
        type="button"
      >
        Next
      </button>
    </div>
    <IndicatorProgressModal
      v-if="displayModal"
      :show-modal="displayModal"
      :modal-title="indicatorProgressionModalContent[currentPageNumber - 1].title"
      :modal-message="indicatorProgressionModalContent[currentPageNumber - 1].message"
      :current-step="currentPageNumber"
      :steps="indicatorSteps"
      @on-next-click="onNextClick"
    />
  </div>
</template>

<script setup lang="ts">
import { isMobile } from "mobile-device-detect";
import { TweenLite, Power1 } from "gsap";
import Debug from "debug";
import axios from "axios";
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import { useRoute } from "vue-router";
import IndicatorProgressModal from "../../components/strengths-mapping/indicator-progress-modal.vue";
import {
  indicatorSteps,
  indicatorProgressionModalContent,
} from "../../components/strengths-mapping/indicator-steps";
import cms from "../../services/cms/cms";
import CmsImg from "../../components/cms/cms-img.vue";
import { csContext } from "../../services/cs-context";
import DraggableStrength from "../../components/strengths-mapping/draggable-strength.vue"; // tslint:disable-line no-var-requires
import StyledCheckbox from "../../components/strengths-mapping/styled-checkbox.vue";
import StrengthsMappingPaging from "../../components/strengths-mapping/strengths-mapping-paging.vue";
import SessionInstanceState from "../../components/sessions/session-instance-state";
import {
  CmsDocumentTypes,
  CmsCustomStrengthMapping,
  CmsCustomStrengthItem,
  CmsImage,
} from "../../services/cms/cms-type-definitions";
import isLiveEnvironment from "../../services/utilities/environment";

const debug = Debug("cs.StrengthsMappingComponent");

const strengthRowGapPercent = 5;

export interface StrengthItemModel {
  ItemId: number;
  DimensionId: number;
  Factor: string;
  Name: string;
  Description: string;
  PositiveIndicators: string[];
  NegativeIndicators: string[];
}

export interface StrengthsMappingModel {
  Id: string;
  IsSelfReview: boolean;
  ViewpointsSubjectFirstName: string | null;
  StrengthItems: StrengthItemModel[];
  Ratings: { [x: string]: number | null };
  CmsCustomStrengthMappingId: string | null;
}

export interface StrengthAndRating {
  strengthId: string;
  cmsCustomStrengthItem?: CmsCustomStrengthItem;
  strengthName: string;
  draggableName: string | null;
  description: string;
  page: number;
  section: 1 | 2;
  rating: number | null;
  order: number;
  positiveIndicators: string[];
  negativeIndicators: string[];
}

export interface PageDetail {
  pageNumber: number;
  isCurrent: boolean;
  isAvailable: boolean;
}

export interface StrengthItemPercentJson {
  Id: string;
  Percent: number;
}

export interface ButtonInfo {
  bigScreenText: string;
  smallScreenText: string;
  action: () => void;
}

export interface CmsMetadata {
  id?: string;
  uid?: string;
  ref?: string;
  type?: string;
  title?: string;
  lastPublished?: string;
}

const strengthsMappingTypes = {
  strengths: "Strengths",
  indicators: "Indicators",
} as const;

export type StrengthsMappingType =
  (typeof strengthsMappingTypes)[keyof typeof strengthsMappingTypes];

export interface StrengthMappingMetadata {
  strengthItems: { [x: string]: CmsMetadata };
  questionnaire?: CmsMetadata;
}

export interface StrengthItemsUpdate {
  ratings: { [x: string]: number | null | undefined };
  meta: StrengthMappingMetadata;
  type?: StrengthsMappingType;
}

const props = withDefaults(
  defineProps<{
    model: StrengthsMappingModel;
    embedded?: boolean;
    numberOfPages?: number;
  }>(),
  {
    embedded: false,
    numberOfPages: 4,
  },
);

const route = useRoute();

const firstCustomPageNumber = ref(5);
const currentStrengthItem = ref<StrengthAndRating | null>(null);
const currentPageNumber = ref(1);
const previousPageNumber = ref(1);
const currentStrengthOrderBeforeDragging = ref<number | null>(null);
const currentStrengthRatingBeforeDragging = ref<number | null>(null);
const currentStrengthBeingDragged = ref<StrengthAndRating | null>(null);
const showMoreInfo = ref(false);
const showOverlay = ref(true);
const showInterstitial = ref(false);
const shouldReopenMoreInfo = ref(false);
const isSaving = ref(false);
const hasBeenDestroyed = ref(false);
const ready = ref(false);
const { isAdmin } = csContext.userProfile;
const doc = ref<CmsCustomStrengthMapping | null>(null);
const allStrengthItems = ref<StrengthAndRating[]>([]);

const strengthsMappingType = computed(() => {
  return props.model.StrengthItems.length === 34
    ? strengthsMappingTypes.strengths
    : strengthsMappingTypes.indicators;
});

const maxPageNumber = computed(() => {
  return allStrengthItems.value.length > 0
    ? allStrengthItems.value[allStrengthItems.value.length - 1].page
    : props.numberOfPages;
});

const isMappingIndicators = computed(() => {
  return strengthsMappingType.value === strengthsMappingTypes.indicators;
});

const displayModal = ref(false);

function setShowMoreInfo(value: boolean): void {
  showMoreInfo.value = value;
}

function getCurrentPageStrengths(): StrengthAndRating[] {
  return allStrengthItems.value.filter((item) => item.page === currentPageNumber.value);
}

function getNextUnratedStrengthOnPage(): StrengthAndRating | null {
  return (
    getCurrentPageStrengths()
      .filter((item) => item.rating === null)
      .find(() => true) || null
  );
}

const nextUnratedStrengthOnPage = computed(() => {
  return getNextUnratedStrengthOnPage();
});

const isSection1Complete = computed(() => {
  return !allStrengthItems.value.some((item) => item.rating === null && item.section === 1);
});

const isReviewComplete = computed(() => {
  return !allStrengthItems.value.some((item) => item.rating === null);
});

const shouldDisplaySectionCompleteMessage = computed(() => {
  return (
    currentStrengthItem.value === null &&
    isSection1Complete.value &&
    currentPageNumber.value < firstCustomPageNumber.value &&
    !isReviewComplete.value
  );
});

const shouldDisplayReviewCompleteMessage = computed(() => {
  return currentStrengthItem.value === null && isReviewComplete.value;
});

const shouldDisplayPageCompleteMessage = computed(() => {
  return (
    currentStrengthItem.value === null &&
    nextUnratedStrengthOnPage.value === null &&
    !shouldDisplaySectionCompleteMessage.value &&
    !shouldDisplayReviewCompleteMessage.value
  );
});

const showIndicatorProgressModal = computed(() => {
  return isMappingIndicators.value && shouldDisplayPageCompleteMessage.value;
});

const pageDetails = computed(() => {
  const pageNumbers = [...Array(maxPageNumber.value)].map((x, i) => i + 1);

  const isPart1 = currentPageNumber.value < firstCustomPageNumber.value;
  const isPart2 = !isPart1;

  return pageNumbers.map((pageNumber) => {
    const isPreviousPageAllRated = allStrengthItems.value.every(
      (i) => i.page !== pageNumber - 1 || i.rating !== null,
    );

    return {
      pageNumber,
      isCurrent: pageNumber === currentPageNumber.value,
      isAvailable:
        (pageNumber <= currentPageNumber.value || isPreviousPageAllRated) &&
        ((isPart1 && pageNumber < firstCustomPageNumber.value) ||
          (isPart2 && pageNumber >= firstCustomPageNumber.value)),
    } as PageDetail;
  });
});

const currentPageStrengths = computed(() => {
  return allStrengthItems.value.filter((item) => item.page === currentPageNumber.value);
});

const currentPageVisibleStrengths = computed(() => {
  return currentPageStrengths.value
    .filter((item) => {
      return (
        item.rating !== null ||
        item === nextUnratedStrengthOnPage.value ||
        item === currentStrengthItem.value
      );
    })
    .sort((a, b) => b.order - a.order);
});

const rowHeightPixelsCached = ref<number | undefined>(undefined);

const strengthDragHeightMeasure = ref();
const ratingArea = ref();
const nextUnratedStrengthOrigin = ref();
const currentStrengthLine = ref();
const submitButton = ref();

const emit = defineEmits(["submit-review"]);

function rowHeightPixels(): number {
  rowHeightPixelsCached.value =
    rowHeightPixelsCached.value ||
    (strengthDragHeightMeasure.value as HTMLElement).getBoundingClientRect().height *
      (1 + strengthRowGapPercent / 100);
  return rowHeightPixelsCached.value;
}

function positionStrengthInRatingArea(strength: StrengthAndRating, immediate = false) {
  const strengthElement = document.querySelector(
    `[data-strength-id="${strength.strengthId}"]`,
  ) as HTMLElement;

  if (strengthElement && strength.rating !== null) {
    const ratingAreaRect = (ratingArea.value as HTMLElement).getBoundingClientRect();
    const strengthElementRect = strengthElement.getBoundingClientRect();
    const x = ratingAreaRect.width * (strength.rating / 100) - strengthElementRect.width / 2;
    const y = -strength.order * rowHeightPixels();
    const ease = currentStrengthBeingDragged.value ? Power1.easeOut : Power1.easeIn;
    TweenLite.to(strengthElement, immediate ? 0 : 0.2, {
      x,
      y,
      ease,
    });
  }
}

function positionAllRatedStrengths(immediate = false) {
  currentPageVisibleStrengths.value
    .filter((s) => s !== currentStrengthBeingDragged.value && s.rating !== null)
    .forEach((strength) => positionStrengthInRatingArea(strength, immediate));
}

function positionStrengthAsNextUnrated(strength: StrengthAndRating | null, immediate = false) {
  if (strength === null) {
    return;
  }

  const strengthElement = document.querySelector(
    `[data-strength-id="${strength.strengthId}"]`,
  ) as HTMLElement;

  if (strengthElement) {
    const originRect = (nextUnratedStrengthOrigin.value as HTMLElement).getBoundingClientRect();
    const ratingAreaRect = (ratingArea.value as HTMLElement).getBoundingClientRect();
    const x = originRect.left - ratingAreaRect.left;
    const y = originRect.bottom - ratingAreaRect.bottom;
    TweenLite.to(strengthElement, immediate ? 0 : 0.2, { x, y });
  }
}

function positionAllStrengths(immediate = false) {
  positionAllRatedStrengths(immediate);
  if (currentStrengthItem.value && currentStrengthItem.value.rating === null) {
    positionStrengthAsNextUnrated(currentStrengthItem.value, immediate);
  } else if (
    currentStrengthItem.value !== nextUnratedStrengthOnPage.value &&
    nextUnratedStrengthOnPage.value
  ) {
    positionStrengthAsNextUnrated(nextUnratedStrengthOnPage.value, immediate);
  }
}

function resetStrengthOrdersForPage() {
  currentPageStrengths.value
    .sort((a, b) => a.order - b.order)
    .forEach((strength: StrengthAndRating, index: number) => {
      strength.order = index;
    });
}

function initializePage() {
  currentStrengthItem.value = getNextUnratedStrengthOnPage();
  resetStrengthOrdersForPage();
  showMoreInfo.value = false;

  nextTick(() => {
    positionAllStrengths(true);
  });
}

function goToPage(pageNumber: number, force?: boolean): void {
  const pageDetail = pageDetails.value.find((i) => i.pageNumber === pageNumber);
  if (pageDetail && (pageDetail.isAvailable || force)) {
    previousPageNumber.value = currentPageNumber.value;
    currentPageNumber.value = pageNumber;

    nextTick(() => {
      initializePage();
    });
  }
}

function onNextClick(): void {
  displayModal.value = false;
  goToPage(currentPageNumber.value + 1);
}

function toggleIndicatorProgressModal(): void {
  displayModal.value = !displayModal.value;
}

const pageCompleteText = computed(() => {
  if (isMappingIndicators.value) {
    return `You've completed the ${indicatorSteps[currentPageNumber.value - 1].title} section`;
  }
  return `Page ${currentPageNumber.value} of ${maxPageNumber.value} completed`;
});

const ratedStrengthsCount = computed(() => {
  return allStrengthItems.value.filter(
    (item) => item.rating !== null && item !== currentStrengthItem.value,
  ).length;
});

const isSelfReview = computed(() => {
  return props.model.IsSelfReview;
});

const viewpointsSubjectFirstName = computed(() => {
  return props.model.ViewpointsSubjectFirstName;
});

const shouldShowInterstitial = computed(() => {
  return showInterstitial.value;
});

const isSection2 = computed(() => {
  return currentPageNumber.value >= firstCustomPageNumber.value;
});

const maxPageNumberForPaging = computed(() => {
  return isSection2.value || strengthsMappingType.value === strengthsMappingTypes.indicators
    ? maxPageNumber.value
    : firstCustomPageNumber.value - 1;
});

const minPageNumberForPaging = computed(() => {
  return isSection2.value ? firstCustomPageNumber.value : 1;
});

function canGoToPage(pageNumber: number): boolean {
  const pageDetail = pageDetails.value.find((i) => i.pageNumber === pageNumber);
  return !!(pageDetail && pageDetail.isAvailable);
}

const canGoToNextPage = computed(() => {
  return canGoToPage(currentPageNumber.value + 1);
});

const canGoToPreviousPage = computed(() => {
  return canGoToPage(currentPageNumber.value - 1);
});

const reviewTitle = computed(() => {
  if (isSection2.value)
    return cms.asText(
      doc.value?.data.pages[currentPageNumber.value - firstCustomPageNumber.value].page_header,
    );

  if (isMappingIndicators.value) {
    return indicatorSteps[currentPageNumber.value - 1].title;
  }

  return isSelfReview.value
    ? "Strengths Mapping"
    : `Your review of ${viewpointsSubjectFirstName.value}`;
});

function buildStrengthItemsFromModel() {
  const totalStrengthPages = 4;

  const normal = props.model.StrengthItems.map((strength, i) => {
    return {
      strengthId:
        strengthsMappingType.value === strengthsMappingTypes.indicators
          ? strength.ItemId
          : strength.Name,

      strengthName: strength.Name,
      draggableName: strength.Name,
      description: strength.Description,
      page:
        strength.DimensionId <= totalStrengthPages
          ? strength.DimensionId
          : strength.DimensionId - totalStrengthPages,
      rating:
        props.model.Ratings[
          strengthsMappingType.value === strengthsMappingTypes.indicators
            ? strength.ItemId
            : strength.Name
        ] ?? null, // Math.random() * 100,
      order: i,
      section: 1,
      positiveIndicators: strength.PositiveIndicators,
      negativeIndicators: strength.NegativeIndicators,
    } as StrengthAndRating;
  });

  const custom = [] as typeof normal;

  let itemIndex = normal[normal.length - 1].order + 1;

  // if Custom Framework exists
  if (doc.value) {
    let pageNumber = 4;

    doc.value.data.pages.forEach((page) => {
      pageNumber += 1;

      const items = [
        page.item_1_embed_link,
        page.item_2_embed_link,
        page.item_3_embed_link,
        page.item_4_embed_link,
        page.item_5_embed_link,
        page.item_6_embed_link,
        page.item_7_embed_link,
        page.item_8_embed_link,
        page.item_9_embed_link,
        page.item_10_embed_link,
      ];

      items.forEach((item) => {
        if (!item || !item.data) {
          return;
        }

        const id = `custom-id:${item.id}`;

        const rating: StrengthAndRating = {
          strengthId: id,
          cmsCustomStrengthItem: item,
          strengthName: cms.asText(item.data.title),
          draggableName: item.data.draggable_name || cms.asText(item.data.title),
          description: cms.asText(item.data.short_definition).replace(/\u00a0/g, " "),
          page: pageNumber,
          rating: props.model.Ratings[id] ?? null,
          order: itemIndex,
          section: 2,
          positiveIndicators: item.data.positive_indicators.map((ind) =>
            cms.asText(ind.description).replace(/\u00a0/g, " "),
          ),
          negativeIndicators: item.data.negative_indicators.map((ind) =>
            cms.asText(ind.description).replace(/\u00a0/g, " "),
          ),
        };

        custom.push(rating);
        itemIndex += 1;
      });
    });
  }

  return normal.concat(custom).filter((item: StrengthAndRating) => item.page >= 1);
}

function goToFirstIncompletePage() {
  const firstUnfinished = allStrengthItems.value.find(
    (si) => si.rating === null || si.rating === undefined,
  );

  goToPage(firstUnfinished?.page ?? maxPageNumber.value, true);
}

function onResize(event: Event) {
  setTimeout(() => {
    if (!hasBeenDestroyed.value) {
      rowHeightPixelsCached.value = undefined;
      positionAllStrengths(true);
    }
  }, 500);
}

function onKeydown(event: KeyboardEvent): void {
  // TODO: review this, but I think this is weird behaviour and it's triggering when it shouldn't
  // if (event.key === "m") {
  //   showMoreInfo.value = !showMoreInfo.value;
  // }
}

onMounted(() => {
  const begin = () => {
    allStrengthItems.value =
      (props.embedded && SessionInstanceState.get(`allStrengthItems-${props.model.Id}`)) ||
      buildStrengthItemsFromModel();

    goToFirstIncompletePage();
    showOverlay.value = ratedStrengthsCount.value === 0; // Show the overlay if we're starting the review from scratch
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    window.addEventListener("keydown", onKeydown);
    ready.value = true;
  };

  let docPromise: Promise<CmsCustomStrengthMapping> | null = null;

  if (route?.params?.uid && route?.meta?.type === CmsDocumentTypes.CmsCustomStrengthMapping) {
    docPromise = cms.getByUid(
      CmsDocumentTypes.CmsCustomStrengthMapping,
      route?.params.uid as string,
    );
  } else if (props.model.CmsCustomStrengthMappingId) {
    docPromise = cms.getById(
      CmsDocumentTypes.CmsCustomStrengthMapping,
      props.model.CmsCustomStrengthMappingId,
    );
  }

  if (docPromise) {
    docPromise.then((docResult) => {
      doc.value = docResult;
      begin();
    });
  } else {
    begin();
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", onResize);
  window.removeEventListener("orientationchange", onResize);
  window.removeEventListener("keydown", onKeydown);
  if (props.embedded && props.model && props.model.Id && allStrengthItems.value) {
    SessionInstanceState.set(`allStrengthItems-${props.model.Id}`, allStrengthItems.value);
  }
  hasBeenDestroyed.value = true;
});

function saveAndExit(): void {
  // TODO...
}

function nextPage(): void {
  goToPage(currentPageNumber.value + 1);
}

function handleNextArrowClick(): void {
  if (shouldDisplayPageCompleteMessage.value && isMappingIndicators.value) {
    toggleIndicatorProgressModal();
  } else {
    nextPage();
  }
}

function previousPage(): void {
  goToPage(currentPageNumber.value - 1);
}

function onStrengthDragClick(element: HTMLElement, strength: StrengthAndRating): void {
  // Mark this strength as the current one
  currentStrengthItem.value = strength;
}

function hideOverlay(): void {
  showOverlay.value = false;
}

function onStrengthDragStart(element: HTMLElement, strength: StrengthAndRating): void {
  currentStrengthItem.value = strength;
  currentStrengthOrderBeforeDragging.value = strength.order;
  currentStrengthRatingBeforeDragging.value = strength.rating;
  currentStrengthBeingDragged.value = strength;
  if (showMoreInfo.value) {
    shouldReopenMoreInfo.value = true;
  }
  showMoreInfo.value = false;
  hideOverlay();
}

function getCurrentPageVisibleStrengths(): StrengthAndRating[] {
  const nextStrengthOnPage = getNextUnratedStrengthOnPage();
  return getCurrentPageStrengths()
    .filter((item) => {
      return (
        item.rating !== null ||
        (item === nextStrengthOnPage && currentStrengthItem.value === null) ||
        item === currentStrengthItem.value
      );
    })
    .sort((a, b) => b.order - a.order);
}

function getCurrentDraggableRating(strengthElement: HTMLElement): number | null {
  const ratingAreaRect = (ratingArea.value as HTMLElement).getBoundingClientRect();
  const draggableStrengthRect = strengthElement.getBoundingClientRect();
  const draggableRatingOffsetX =
    draggableStrengthRect.left + draggableStrengthRect.width / 2 - ratingAreaRect.left;
  const ratingFraction = draggableRatingOffsetX / ratingAreaRect.width;
  return ratingFraction >= 0 && ratingFraction <= 1 ? ratingFraction * 100 : null;
}

function getCurrentDraggableOrder(strengthElement: HTMLElement): number | null {
  const ratingAreaRect = (ratingArea.value as HTMLElement).getBoundingClientRect();
  const draggableStrengthRect = strengthElement.getBoundingClientRect();
  const draggableOffsetY =
    ratingAreaRect.bottom - draggableStrengthRect.bottom + draggableStrengthRect.height;
  const order = Math.floor(draggableOffsetY / rowHeightPixels());
  const clampedOrder =
    order >= 0 && draggableOffsetY < ratingAreaRect.height
      ? Math.min(order, getCurrentPageVisibleStrengths().length)
      : null;
  return clampedOrder;
}

function positionRatingLine(element: HTMLElement) {
  if (currentStrengthBeingDragged.value) {
    const strengthRect = element.getBoundingClientRect();
    const ratingAreaRect = (ratingArea.value as HTMLElement).getBoundingClientRect();
    const offsetX = strengthRect.left - ratingAreaRect.left + strengthRect.width / 2;
    const offsetY = strengthRect.bottom - ratingAreaRect.top;
    (currentStrengthLine.value as HTMLElement).classList.remove("hidden");
    TweenLite.set(currentStrengthLine.value, {
      left: offsetX,
      top: offsetY,
    });
  }
}

function hideRatingLine() {
  (currentStrengthLine.value as HTMLElement).classList.add("hidden");
  TweenLite.set(currentStrengthLine.value, { top: "100%" });
}

function onStrengthDragMove(element: HTMLElement, strength: StrengthAndRating): void {
  const newRating = getCurrentDraggableRating(element);
  const newOrder = getCurrentDraggableOrder(element);
  if (newRating !== null && newOrder !== null) {
    if (newOrder !== strength.order) {
      // Re-order the strengths
      strength.order = newOrder - 0.5;
      resetStrengthOrdersForPage();
      positionAllRatedStrengths();
    }

    positionRatingLine(element);
  } else {
    hideRatingLine();
  }
}

function saveChanges(): Promise<boolean> {
  if (isSaving.value) {
    // TODO: Queue the request? If the request is ignored like this, data may be lost in exceptional circumstances
    return Promise.resolve(false); // Only one save operation at a time
  }

  const update: StrengthItemsUpdate = {
    ratings: {},
    meta: {
      strengthItems: {},
      questionnaire: doc.value // this is for custom frameworks
        ? {
            id: doc.value.id,
            uid: doc.value.uid,
            ref: cms.getRef(doc.value) || undefined,
            type: doc.value.type,
            title: cms.asText(doc.value.data.title),
            lastPublished: doc.value.last_publication_date,
          }
        : undefined,
    },
    type: strengthsMappingType.value,
  };

  allStrengthItems.value.forEach((sAndR) => {
    if (sAndR) {
      update.ratings[sAndR.strengthId] = sAndR.rating;

      if (sAndR.cmsCustomStrengthItem) {
        update.meta.strengthItems[sAndR.strengthId] = {
          id: sAndR.cmsCustomStrengthItem.id,
          uid: sAndR.cmsCustomStrengthItem.uid,
          ref: cms.getRef(sAndR.cmsCustomStrengthItem) || undefined,
          type: sAndR.cmsCustomStrengthItem.type,
          title: cms.asText(sAndR.cmsCustomStrengthItem.data.title),
          lastPublished: sAndR.cmsCustomStrengthItem.last_publication_date,
        };
      }
    }
  });

  isSaving.value = true;
  return axios
    .post(`/Review/ChangeQuestionnaire/${props.model.Id}`, update)
    .then((e) => {
      isSaving.value = false;
      return true; // Success
    })
    .catch((e) => {
      isSaving.value = false;
      return false; // Failed
    });
}

function onStrengthDragEnd(element: HTMLElement, strength: StrengthAndRating): void {
  const newRating = getCurrentDraggableRating(element);
  const newOrder = getCurrentDraggableOrder(element);
  if (newRating !== null && newOrder != null) {
    strength.rating = newRating;
    strength.order = newOrder;
    saveChanges();
  } else {
    strength.order = (currentStrengthOrderBeforeDragging.value || 0) - 0.5;
    strength.rating = currentStrengthRatingBeforeDragging.value;
  }

  currentStrengthOrderBeforeDragging.value = null;
  currentStrengthRatingBeforeDragging.value = null;
  currentStrengthBeingDragged.value = null;
  currentStrengthItem.value = getNextUnratedStrengthOnPage();
  resetStrengthOrdersForPage();
  hideRatingLine();
  nextTick(() => {
    positionAllRatedStrengths();
    positionStrengthAsNextUnrated(
      currentStrengthItem.value,
      !(currentStrengthItem.value === strength && currentStrengthItem.value.rating === null),
    );
    if (currentStrengthItem.value && shouldReopenMoreInfo.value) {
      showMoreInfo.value = true;
    }
    shouldReopenMoreInfo.value = false;
  });
}

function clearSelected(event: Event): void {
  if (currentStrengthItem.value && currentStrengthItem.value.rating !== null) {
    currentStrengthItem.value = getNextUnratedStrengthOnPage();
    showMoreInfo.value = false;

    if (currentStrengthItem.value) {
      nextTick(() => {
        positionStrengthAsNextUnrated(currentStrengthItem.value, true);
      });
    }
  }
}

async function submitReview(): Promise<void> {
  if (!props.embedded) {
    return saveChanges()
      .then((success) => {
        if (!success) {
          throw new Error("Failed to submit ratings.");
        }
        window.location.href = `/Review/MappingStrengths/${props.model.Id}?completeReport=true`;
      })
      .catch((e) => {
        alert("Failed to submit your review, please try again.");
      });
  }

  return saveChanges()
    .then((success) => {
      if (!success) {
        throw new Error("Failed to submit ratings.");
      }
      return axios
        .get(`/Review/MappingStrengths/${props.model.Id}?completeReport=true`)
        .then((r) => {
          emit("submit-review");
        });
    })
    .catch((e) => {
      alert("Failed to submit your review, please try again.");
    });
}

const interstitialImageSrc = computed(() => {
  return doc?.value?.data?.interstitial_full_size_image;
});

function goToPart2() {
  if (interstitialImageSrc.value?.url) {
    showInterstitial.value = true;
  }
  goToPage(firstCustomPageNumber.value, true);
}

const buttonInfo = computed(() => {
  if (isReviewComplete.value) {
    return {
      bigScreenText: "Submit ratings",
      smallScreenText: "Submit",
      action: () => submitReview(),
    };
  }
  if (isSection1Complete.value && currentPageNumber.value < firstCustomPageNumber.value) {
    return {
      bigScreenText: "Continue",
      smallScreenText: "Continue",
      action: () => goToPart2(),
    };
  }
  if (shouldDisplayPageCompleteMessage.value) {
    return {
      bigScreenText: "Continue",
      smallScreenText: "Continue",
      action: isMappingIndicators.value
        ? () => toggleIndicatorProgressModal()
        : () => goToPage(currentPageNumber.value + 1),
    };
  }
  return {
    bigScreenText: "",
    smallScreenText: "",
    action: () => {
      // Do nothing
    },
  };
});

function hideInterstitial(): void {
  showInterstitial.value = false;
}

function goFullScreen() {
  const elem = document.documentElement as any;
  if (elem.requestFullscreen) {
    elem.requestFullscreen();
  } else if (elem.mozRequestFullScreen) {
    /* Firefox */
    elem.mozRequestFullScreen();
  } else if (elem.webkitRequestFullscreen) {
    /* Chrome, Safari and Opera */
    elem.webkitRequestFullscreen();
  } else if (elem.msRequestFullscreen) {
    /* IE/Edge */
    elem.msRequestFullscreen();
  } else {
    debug("Fullscreen API not supported");
  }
}

// Returns true on success, false otherwise

function autoCompleteStrengthMapping(): void {
  allStrengthItems.value.forEach((item) => {
    item.rating = Math.floor(Math.random() * 100) + 1;
  });
  hideOverlay();
  positionAllRatedStrengths();
  goToFirstIncompletePage();
  saveChanges();
  (submitButton.value as HTMLButtonElement).style.transitionDelay = "1s";
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
@import "../../components/strengths-mapping/strengths-mapping-variables.less";
@import (reference) "../../styles/bootstrap/variables.less";
@import (reference) "../../styles/bootstrap/utilities.less";

@section-1-theme: @charismatic-color;
@section-2-theme: rgb(243, 161, 35);
@more-info-color: @bright-pink;
@current-strength-line-color: @bright-pink;
@main-area-background-color: #f2f2f2;
@overlay-background-color: rgba(0, 0, 0, 0.4);
@overlay-text-background-color: white;
@orientation-overlay-background-color: @dark-background-color;
@orientation-overlay-text-color: white;
@shutter-border-color: @navbar-border-color;
@shutter-background-color: white;
@mobile-scroll-arrow-color: @bright-pink;
@overlay-inverse-background-color: rgba(255, 255, 255, 0.9);
@navbar-font-color: black;
@navbar-background-color: white;
@navbar-border-color: darkgray;
@navbar-disabled-link-color: #eeeeee;

// Navbar
@navbar-border-width: 1px;
@navbar-font-size: 1.2em;

// Subnavbar
@current-strength-notch-size: 0.5em;
@more-info-width: 10em;
@current-strength-description-right-margin: 0.85 * @more-info-width;
@mobile-submit-button-wrapper-width: 8em;

// Rating area
@rating-scale-image-height: 40%;
@rating-scale-bottom-offset: 0em;
@rating-area-border-radius: 0.4em;
@rating-strength-line-thickness: 1px;
@rating-scale-font-size: 0.9em;
@rating-area-drop-shadow-thickness: 0.4em;
// Shutters
@shutter-gap-width: 1em;
@shutter-padding-mobile: 4%;
@shutter-padding: 2em; // 4%;
@shutter-indicator-font-size-desktop: 2.8vh;
@shutter-indicator-item-bottom-padding: 0.6em;
@shutter-indicator-item-bottom-padding-small-mobile: 0em;

// Overlay
@overlay-font-size: 2em;
@overlay-border-radius: 0.2em;
@overlay-max-width: 50%;
@overlay-padding: 0.5em;

@viewport {
  orientation: landscape;
}

.strengths-mapping {
  width: 100vw;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  background-color: @dark-background-color;
  position: relative; // Capture overlays
  overflow: hidden;

  .rating-interface-wrapper {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
  }

  .rating-interface {
    position: relative; // To catch the shutters
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
  }

  // Unsupported
  @media @media-query-for-not-supported {
    visibility: hidden !important;
    // Ensure the checkbox doesn't pop through the orientation display
    .styled-checkbox {
      display: none !important;
    }
  }

  .unsupported-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: @orientation-overlay-background-color;
    display: none;
    justify-content: center;
    align-items: center;

    .unsupported-overlay-text {
      color: @orientation-overlay-text-color;
      text-align: center;
      pointer-events: none;
      min-width: 100vw !important;
      font-size: 1.8em !important;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    @media @media-query-for-not-supported {
      display: flex;
      visibility: visible;
    }
  }

  // Orientation
  @media @media-query-for-suggesting-orientation-change {
    .is-mobile-device & {
      visibility: hidden !important;
      // Ensure the checkbox doesn't pop through the orientation display
      .styled-checkbox {
        display: none !important;
      }
    }
  }

  .orientation-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: @orientation-overlay-background-color;
    display: none;
    justify-content: center;
    align-items: center;

    @media @media-query-for-suggesting-orientation-change {
      .is-mobile-device & {
        display: flex;
        visibility: visible;
        pointer-events: all;
      }
    }

    @media @media-query-for-not-supported {
      display: none !important;
    }

    .orientation-overlay-text {
      color: @orientation-overlay-text-color;
      // padding: @overlay-padding;
      text-align: center;
      pointer-events: none;
      min-width: 100vw !important;
      font-size: 1.8em !important;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      & > img {
        display: inline-block;
        max-width: 60vw;
        height: auto;
      }

      span {
        display: block;
        min-width: 100%;
        margin-top: 1em;
        padding: 0.2em;
        // white-space: nowrap;

        img {
          display: inline-block;
          height: 1em;
        }
      }

      span.inverse {
        color: @orientation-overlay-background-color;
        background-color: @overlay-inverse-background-color;
      }
    }
  }

  // Base styling
  a {
    text-decoration: none;
    cursor: pointer;
    color: @link-color;

    &:hover {
      @media (pointer: fine) {
        color: @link-hover-color;
      }
    }

    &[disabled] {
      color: @disabled-link-color;
      cursor: not-allowed;
    }
  }
}

.strengths-mapping-mobile-view .subnavbar {
  .page-complete-message {
    margin-right: 8em;
  }
}

.auto-complete-btn-container {
  .center-flex();
  position: absolute;
  left: 3em;
  height: 100%;
}

.strength-name-text {
  font-weight: bold;
  color: @dark-background-color;
}

.strengths-mapping-navbar {
  width: 100%;
  z-index: @z-index-navbar;
  position: relative;
  background-color: @navbar-background-color;
  border-bottom: @navbar-border-width solid @navbar-border-color;
  display: flex;
  flex-direction: row;
  justify-content: center;

  span {
    display: block;
    height: 100%;
    text-decoration: none;
    line-height: @navbar-height * 0.8;
    color: @dark-background-color;
    font-size: @navbar-font-size;
  }

  a.home-link {
    display: block;
    position: absolute;
    right: 1em;
    line-height: @navbar-height * 0.8;
  }
}

.subnavbar {
  width: 100%;
  border-bottom: @subnavbar-border-width solid @navbar-border-color;
  font-size: @subnavbar-font-size;
  background-color: @subnavbar-background-color;
  display: flex;
  flex-direction: row;
  align-items: stretch;

  .drag-start {
    display: flex;
    background-color: @section-1-theme;
    // transition: background-color 1s;

    .section-2-theme & {
      background-color: @section-2-theme;
    }

    box-sizing: border-box;
    justify-content: center;
    align-items: center;
    position: relative; // To allow absolute positioning of mobile paging, etc.

    .drag-origin-wrapper {
      pointer-events: none;
      position: relative;
      border: @strength-border-width solid transparent;

      &::after {
        content: " ";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: @strength-border-width solid transparent;
        // border-radius: @strength-border-radius;
        box-sizing: border-box;

        .has-unrated-selected-strength & {
          //border-color: white;
        }
      }

      .review-complete &, // I do not know if this line does anything.
      .show-button & {
        visibility: hidden;
        position: absolute;
        right: 100%;
      }
    }
  }

  .submit-button {
    opacity: 0;
    //transition: opacity 1s;
    //transition-delay: 5s;
    padding-left: 2em;
    padding-right: 2em;
    visibility: hidden;
    position: absolute;

    .show-button & {
      opacity: 1;
      visibility: visible;
      position: static;
    }
  }

  .current-strength {
    flex: 10; // Fill up remaining space
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;

    & > .header {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: stretch;
      padding-left: @current-strength-horizontal-padding;
      background-color: @off-white;
      border-bottom: 1px solid @current-strength-header-border-color;

      .header-text {
        background-color: @more-info-header-color;
        padding-right: @current-strength-horizontal-padding;
      }

      .header-line {
        flex: 10;
        line-height: 0;
        margin-right: @current-strength-horizontal-padding;
        border-top: 1px solid @section-1-theme;
        //transition: border-top-color 1s;

        .section-2-theme & {
          border-top-color: @section-2-theme;
        }
      }
    }

    p {
      margin: 0;
      padding: @current-strength-vertical-padding @current-strength-horizontal-padding;

      &:not(.page-complete-message) {
        margin-right: @current-strength-description-right-margin;
      }
    }

    &::before {
      // Left-side notch
      content: " ";
      position: absolute;
      top: calc(50% - @current-strength-notch-size);
      left: calc(-@current-strength-notch-size + 2px);
      width: 0;
      height: 0;
      border: @current-strength-notch-size solid white;
      border-right-color: transparent;
      border-top-color: transparent;
      transform: rotateZ(45deg);
      box-sizing: content-box;
      display: none;

      .has-unrated-selected-strength:not(.is-dragging-strength) & {
        // Only display when a strength is selected but not being dragged
        display: block;
      }
    }

    .mobile-submit-button-wrapper {
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: @mobile-submit-button-wrapper-width;
      overflow: hidden;
      pointer-events: none;

      .mobile-submit-button-inner {
        position: absolute;
        top: 0;
        right: 0;
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background-color: @section-1-theme;
        //transition: background-color 1s;

        .section-2-theme & {
          background-color: @section-2-theme;
        }

        transition: margin-right 0.5s;
        margin-right: -@mobile-submit-button-wrapper-width;

        .show-button & {
          margin-right: 0;
        }

        .mobile-submit-button {
          pointer-events: all;
        }
      }
    }

    .current-strength-more-info {
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      justify-content: space-between;
      padding-right: @current-strength-horizontal-padding;
      transition: margin-right 0.5s;
      overflow: hidden; // To allow sliding out using margin-right of children
      cursor: pointer;

      & > * {
        transition: margin-right 0.5s;

        .display-complete-message & {
          margin-right: -@more-info-width;
        }
      }

      .header {
        color: @more-info-color;
        font-weight: normal;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;

        .header-text {
          padding-left: @current-strength-horizontal-padding;
        }
      }

      .more-info-checkbox-wrapper {
        text-align: right;
        line-height: 0;
        padding: @current-strength-vertical-padding @current-strength-horizontal-padding;
        padding-right: 0;
        padding-bottom: 0;
        min-width: @current-strength-description-right-margin;
        flex: 10;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none; // Although clicking on the checkbox works, let the parent element handle it

        & > * {
          font-size: @more-info-font-size;
        }
      }

      &:hover {
        .header-text {
          color: @link-hover-color;
        }
      }
    }
  }
}

.main-area {
  background-color: @off-white;
}

.rating-area {
  position: relative;
  margin: @rating-area-default-top-margin @rating-area-horizontal-margin @rating-area-bottom-margin
    @rating-area-horizontal-margin;
  background-color: white;
  border-radius: @rating-area-border-radius;
  pointer-events: none;

  &::before,
  &::after {
    content: " ";
    position: absolute;
    top: 0;
    bottom: 0;
    width: 400px;
    background-color: @main-area-background-color;
    opacity: 0.8;
    z-index: @z-index-depth-overlay;
  }

  &::before {
    right: 100%;
  }

  &::after {
    left: 100%;
  }

  .depth-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: @rating-area-border-radius;
    box-shadow: inset 0 0 @rating-area-drop-shadow-thickness rgba(0, 0, 0, 0.3);
    z-index: @z-index-depth-overlay;
  }

  .strength-drag-height-measure {
    position: absolute;
    display: block;
    bottom: 0;
    left: 0;
    visibility: hidden;
    pointer-events: none;
    z-index: -1;
  }

  .current-strength-line {
    width: 0;
    position: absolute;
    top: 0;
    bottom: (@rating-scale-height + @rating-scale-bottom-offset);
    left: -10%;
    border-left: @rating-strength-line-thickness @current-strength-line-color solid;
    z-index: @z-index-current-strength-line;

    &.hidden {
      display: none !important;
    }
  }

  .rating-scale {
    position: absolute;
    bottom: @rating-scale-bottom-offset;
    color: @gray-dark;
    left: 0;
    width: 100%;
    height: @rating-scale-height;
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: flex-end;

    .rating-scale-image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 40%;
      max-height: 40%;
      background-image: url("../../assets/strengths-mapping/strengths-mapping-scale.svg");

      .section-2-theme & {
        background-image: url("../../assets/strengths-mapping/strengths-mapping-scale-exec.svg");
      }

      background-size: 100% 100%;
    }

    span {
      padding: 0;
      white-space: nowrap;
      font-size: @rating-scale-font-size;
    }
  }

  .ratings {
    position: absolute;
    bottom: @rating-scale-height + @rating-scale-bottom-offset;
    left: 0;
    width: 100%;
    height: calc(100% - (@rating-scale-height + @rating-scale-bottom-offset));
    display: flex;
    flex-direction: column-reverse;
  }
}

.shutter {
  position: absolute;
  bottom: 0;
  width: calc(50% - @shutter-gap-width);
  z-index: @z-index-shutters;
  border: @shutter-border-width solid @shutter-border-color;
  background-color: @shutter-background-color;
  box-sizing: border-box;
  transition: left 0.5s, right 0.5s;
  overflow: hidden;
  pointer-events: all;

  h4 {
    font-size: @shutter-heading-font-size-in-em;
    font-weight: normal;
    margin-top: 0;
    text-align: center;
    //background: url("../../assets/strengths-mapping/strengths-mapping-scale-gradient-only.svg")
    //  no-repeat;
    background-color: @off-white;
    background-size: 200% 100%;
    background-position-y: top;
    margin-bottom: 0;
    border-bottom: @shutter-border-width solid @shutter-border-color;
    line-height: @shutter-heading-line-height-in-em;

    .section-2-theme & {
      background-image: url("../../assets/strengths-mapping/strengths-mapping-scale-exec-gradient-only.svg");
    }
  }

  ul {
    padding: @shutter-padding-mobile @shutter-padding-mobile*2;
    margin: 0;

    li {
      margin-bottom: @shutter-indicator-item-bottom-padding;
    }
  }
}

.left-shutter {
  left: -2px;

  h4 {
  }

  &.left-shutter-transition-leave-to,
  &.left-shutter-transition-enter {
    left: -100%;
  }

  h4 {
    background-position-x: left;
  }
}

.right-shutter {
  right: -2px;

  &.right-shutter-transition-leave-to,
  &.right-shutter-transition-enter {
    right: -100%;
  }

  h4 {
    background-position-x: right;
  }
}

.nav-arrow {
  position: absolute;
  bottom: 50%;
  z-index: @z-index-nav-arrow;
  pointer-events: all;
  padding: 4em 0; // Extend the clickable area vertically
  margin-bottom: -4em; // Correct positioning change caused by the padding
  transition: opacity 0.1s;

  span {
    font-size: 300%;
  }

  &:active,
  &:focus {
    opacity: 0.3;
  }
}

.overlay {
  opacity: 0;
  transition: opacity 0.5s;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: @overlay-background-color;
  z-index: @z-index-overlay;
  pointer-events: none;
  display: flex;
  justify-content: center;
  align-items: center;

  .overlay-text {
    background-color: @overlay-text-background-color;
    border-radius: @overlay-border-radius;
    font-size: @overlay-font-size;
    max-width: @overlay-max-width;
    padding: @overlay-padding;
    text-align: center;
    pointer-events: none;
  }
}

.show-overlay {
  .overlay {
    opacity: 1;
    pointer-events: all;
  }

  .more-info-checkbox-wrapper {
    z-index: unset; // Ensure the checkbox is behind the overlay when visible
  }
}

.mobile-scroll-wrapper {
  background-color: @dark-background-color;
  min-width: 100%;
  min-height: 100vh;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  background: url(../../assets/sessions/tree-coloured.png);
  background-position: left center;
  background-size: contain;
  background-repeat: no-repeat;

  @media @media-query-for-suggesting-orientation-change {
    & {
      min-height: 0;
      height: 0;
    }
  }

  p {
    color: white;
    font-size: 2em;
    text-align: center;
    padding: 0.4em;

    .glyphicon {
      display: block;
      min-width: 100%;
      color: @mobile-scroll-arrow-color;
    }

    &:first-child {
      margin-top: 2em;
      font-size: 2.2em;
    }
  }

  .inverse {
    color: @dark-background-color;
    background-color: @overlay-inverse-background-color;
    min-width: 100%;
  }

  a.mobile-home-link {
    position: absolute;
    top: 1em;
    right: 1em;
  }
}

.transition-strengths-item {
  display: block;
}

.transition-strengths-enter-active {
  transition: opacity 1s;
}

.transition-strengths-leave-active {
  transition: opacity 2s;
}

.transition-strengths-enter,
.transition-strengths-leave-to {
  opacity: 0;
}

.strengths-mapping-interstitial {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: white;
  display: flex;
  flex-direction: column;
  z-index: 999999999;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: 800px) {
    flex-direction: row;
    justify-content: space-around;
  }

  .strengths-mapping-interstitial-button {
    position: relative;
    top: -50px;

    @media screen and (max-width: 800px) {
      top: unset;
      margin: 0 20% 0 0;
    }
  }

  .strengths-mapping-interstitial-image {
    width: 100%;
    max-height: 100vh;

    @media screen and (max-width: 800px) {
      width: 75%;
    }
  }
}
</style>
